import React, { useState, useMemo, useCallback } from 'react'

import { useClawPaymentOrder } from '../hooks/useClawPaymentOrder'
import PaymentQRCode from '../../components/VipPaymentModal/components/PaymentQRCode'
import { getClawGoods, ClawPlanKind } from '../clawGoods'
import type { GoodsItem } from '../../components/VipPaymentModal/types'
import sensors from '../../../utils/sensors'
import { useAppSelector } from '../../../app/hooks'
import ClawPaymentSuccessModal, {
  PaymentSuccessInfo
} from './ClawPaymentSuccessModal'
import clawIcon from '../assets/claw_icon.png'
import smailClawIcon from '../assets/smail_claw.png'
import midClawIcon from '../assets/mid_claw.png'
import './ClawPaymentModal.scss'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'

interface PlanFeature {
  text: string
  highlight?: boolean
}

interface PlanInfo {
  key: ClawPlanKind
  name: string
  price: number
  features: PlanFeature[]
}

interface Props {
  visible: boolean
  plan: PlanInfo | null
  onClose: () => void
  onTrack?: (actionType: any) => void
}

const PLAN_ICONS: Record<ClawPlanKind, string> = {
  starter: smailClawIcon,
  standard: midClawIcon,
  flagship: clawIcon
}

const ClawPaymentModal: React.FC<Props> = ({
  visible,
  plan,
  onClose,
  onTrack
}) => {
  const groupID = useAppSelector((state: any) => state.group.groupId)
  const user = useAppSelector((state: any) => state.main.user)
  const group = useAppSelector((state: any) => state.group)
  const [successInfo, setSuccessInfo] = useState<PaymentSuccessInfo | null>(
    null
  )

  const goodsItem: GoodsItem | null = useMemo(() => {
    if (!plan) return null
    return getClawGoods(plan.key)
  }, [plan])

  /** 支付成功 → 调用 webhook 通知（最多重试3次）→ 显示成功/失败弹窗 */
  const handlePaymentSuccess = useCallback(
    async (orderId: string) => {
      console.log('[ClawPayment] onPaymentSuccess 被调用, orderId:', orderId)

      const nickname = user?.nickname || ''
      const mobile = user?.mobile || ''
      const userId = user?.user_id || ''
      const groupId = group?.groupId || ''
      const teamId = group?.invite_id || group?.groupId || ''
      const teamName = group?.groupName || ''

      const webhookUrl =
        process.env.NODE_ENV === 'production'
          ? '/webhook/flow/c5a5e841980e5bdb7d70155b'
          : '/webhook/flow/c5a5e841980e5bdb7d70155b'

      const requestData = {
        key: '今日龙虾Claw',
        order_id: orderId,
        title: '数字员工激活费',
        contact: nickname,
        phone: mobile,
        name: nickname,
        team_id: teamId,
        group_id: groupId,
        team_name: teamName,
        user_id: userId,
        purchase_type: plan?.name || 'Claw数字员工激活费',
        payment_amount: plan?.price || 0
      }

      // 最多重试 3 次，间隔 2s / 4s / 6s
      const MAX_RETRIES = 3
      let webhookFailed = true

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          console.log(
            `[ClawPayment] webhook 第${attempt}次尝试, orderId:`,
            orderId
          )
          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData)
          })
          if (response.ok) {
            console.log('[ClawPayment] webhook 调用成功')
            webhookFailed = false
            break
          }
          console.error(
            `[ClawPayment] webhook 第${attempt}次失败:`,
            response.status
          )
        } catch (error) {
          console.error(`[ClawPayment] webhook 第${attempt}次异常:`, error)
        }
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, attempt * 2000))
        }
      }

      // webhook 完成 → 切换到成功/失败界面
      setSuccessInfo({
        orderId,
        planName: plan?.name || '',
        amount: plan?.price || 0
      })

      sensors.track('claw_payment_success', {
        planKey: plan?.key,
        planName: plan?.name,
        price: plan?.price,
        groupID
      })
    },
    [user, group, plan, groupID]
  )

  const handleClose = () => {
    setSuccessInfo(null)
    onClose()
  }

  const {
    alipayUrl,
    wechatUrl,
    qrCodeInvalid,
    channel,
    setChannel,
    isCreatingOrder,
    createOrder,
    clearTimers
  } = useClawPaymentOrder(visible ? goodsItem : null, {
    onPaymentSuccess: handlePaymentSuccess
  })

  // 弹窗关闭时清除轮询定时器，避免 order/query 接口持续调用
  React.useEffect(() => {
    if (!visible) {
      clearTimers()
    }
  }, [visible, clearTimers])

  // 弹窗打开时上报埋点
  React.useEffect(() => {
    if (visible && plan) {
      sensors.track('claw_payment_modal_show', {
        planKey: plan.key,
        planName: plan.name,
        price: plan.price,
        groupID
      })
    }
  }, [visible, plan, groupID])

  if (!visible || !plan) return null

  // ========== 支付成功 → 显示成功弹窗组件 ==========
  if (successInfo) {
    return (
      <ClawPaymentSuccessModal
        visible
        info={successInfo}
        onClose={handleClose}
      />
    )
  }

  // ========== 支付界面 ==========
  const iconSrc = PLAN_ICONS[plan.key] || clawIcon

  return (
    <div className="claw-payment-modal" onClick={onClose}>
      <div
        className="claw-payment-modal__card"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="claw-payment-modal__close" onClick={onClose}>
          <CloseOutlined />
        </button>

        <div className="claw-payment-modal__body">
          {/* 左侧：套餐信息 */}
          <div className="claw-payment-modal__left">
            <div className="claw-payment-modal__plan-icon">
              <img src={iconSrc} alt={plan.name} />
            </div>
            <h2 className="claw-payment-modal__plan-name">{plan.name}</h2>
            <div className="claw-payment-modal__plan-price">
              <span className="claw-payment-modal__currency">¥</span>
              <span className="claw-payment-modal__amount">{plan.price}</span>
            </div>
            <ul className="claw-payment-modal__features">
              {plan.features.map((f, idx) => (
                <li
                  key={idx}
                  className={
                    f.highlight ? 'claw-payment-modal__feature--highlight' : ''
                  }
                >
                  <CheckOutlined />
                  <span>{f.text}</span>
                </li>
              ))}
            </ul>
            <div className="claw-payment-modal__guarantee">
              ✅ 内测免费使用 · 正式版全额抵扣 · 不满意随时退
            </div>
          </div>

          {/* 右侧：支付二维码 */}
          <div className="claw-payment-modal__right">
            <PaymentQRCode
              alipayUrl={alipayUrl}
              wechatUrl={wechatUrl}
              qrCodeInvalid={qrCodeInvalid}
              onRefresh={() => createOrder(true)}
              channel={channel}
              price={plan.price}
              setChannel={(c: number) => {
                setChannel(c)
              }}
              alipayOnly={false}
              isContinuous={false}
              isCreatingOrder={isCreatingOrder}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClawPaymentModal
